import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

/**
 * Active Account Store
 * 
 * Manages the "active" account context for the application, separate from the sign-in account.
 * 
 * - Pro users (accessType 10-19): activeAccountId is locked to signInAccountId
 * - SRA users (accessType 20-29): activeAccountId can be switched to manage different accounts
 * 
 * The active account ID is used in backend requests (AccountPracInfoRequestDto, PracSiteUpdateDto, etc.)
 * while authentication uses the sign-in account credentials.
 */

interface ActiveAccountState {
  // The account ID used for authentication (from login)
  signInAccountId: string | null;
  
  // The account ID currently being worked on/managed (may differ for SRA users)
  activeAccountId: string | null;
  
  // Display name for the active account
  activeAccountName: string | null;
  
  // Whether the current user can switch accounts (accessType 20-29)
  canSwitchAccount: boolean;
}

interface ActiveAccountActions {
  /**
   * Set the sign-in account (called on login)
   * Also sets activeAccountId to the same value initially
   */
  setSignInAccount: (accountId: string, accountName: string, canSwitch: boolean) => void;
  
  /**
   * Set the active account (called when SRA user switches accounts)
   * Only allowed if canSwitchAccount is true
   */
  setActiveAccount: (accountId: string, accountName: string) => void;
  
  /**
   * Reset the store (called on logout)
   */
  reset: () => void;
  
  /**
   * Get the current active account ID (convenience method)
   */
  getActiveAccountId: () => string | null;
}

type ActiveAccountStore = ActiveAccountState & ActiveAccountActions;

const initialState: ActiveAccountState = {
  signInAccountId: null,
  activeAccountId: null,
  activeAccountName: null,
  canSwitchAccount: false,
};

export const useActiveAccountStore = create<ActiveAccountStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    setSignInAccount: (accountId: string, accountName: string, canSwitch: boolean) => {
      set({
        signInAccountId: accountId,
        activeAccountId: accountId, // Initially, active = sign-in
        activeAccountName: accountName,
        canSwitchAccount: canSwitch,
      });
    },

    setActiveAccount: (accountId: string, accountName: string) => {
      const state = get();
      
      // Enforce constraint: Pro users can't switch accounts
      if (!state.canSwitchAccount) {
        console.warn('activeAccountStore: Cannot switch account - user does not have permission');
        return;
      }
      
      set({
        activeAccountId: accountId,
        activeAccountName: accountName,
      });
      
      // Persist active account selection to storage (for SRA users only)
      if (typeof window !== 'undefined') {
        const storage = (window as any).__proGroupsStorage || (window as any).__childNavStorage;
        if (storage?.setItem) {
          void storage.setItem('active_account_id', accountId);
          void storage.setItem('active_account_name', accountName);
        }
      }
    },

    reset: () => {
      set(initialState);
    },

    getActiveAccountId: () => {
      return get().activeAccountId;
    },
  }))
);

// Global registry for external access (non-React contexts)
if (typeof globalThis !== 'undefined') {
  (globalThis as any).__proGroupsActiveAccountStore = useActiveAccountStore;
}
